import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import { bankReconciliationKeys } from '@/hooks/useBankReconciliation'
import type {
  GlAccount,
  BankOpeningBalance,
  JournalEntry,
  FixedAsset,
  IncomeStatement,
  BalanceSheet,
  CashFlow,
  ApiResponse,
} from '@/types'

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const accountingKeys = {
  glAccounts: {
    all: ['gl_accounts'] as const,
    openingBalance: () => [...accountingKeys.glAccounts.all, 'opening_balance'] as const,
    lists: () => [...accountingKeys.glAccounts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...accountingKeys.glAccounts.lists(), filters] as const,
    details: () => [...accountingKeys.glAccounts.all, 'detail'] as const,
    detail: (id: number) => [...accountingKeys.glAccounts.details(), id] as const,
  },
  journalEntries: {
    all: ['journal_entries'] as const,
    lists: () => [...accountingKeys.journalEntries.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...accountingKeys.journalEntries.lists(), filters] as const,
    details: () => [...accountingKeys.journalEntries.all, 'detail'] as const,
    detail: (id: number) => [...accountingKeys.journalEntries.details(), id] as const,
  },
  financialStatements: {
    incomeStatement: (params: Record<string, unknown>) =>
      ['income_statement', params] as const,
    balanceSheet: (params: Record<string, unknown>) =>
      ['balance_sheet', params] as const,
    cashFlow: (params: Record<string, unknown>) =>
      ['cash_flow', params] as const,
  },
  fixedAssets: {
    all: ['fixed_assets'] as const,
    lists: () => [...accountingKeys.fixedAssets.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...accountingKeys.fixedAssets.lists(), filters] as const,
    details: () => [...accountingKeys.fixedAssets.all, 'detail'] as const,
    detail: (id: number) => [...accountingKeys.fixedAssets.details(), id] as const,
  },
}

// ─── GL Accounts ─────────────────────────────────────────────────────────────

export function useGlAccounts(params: { active?: boolean } = {}) {
  return useQuery({
    queryKey: accountingKeys.glAccounts.list(params as unknown as Record<string, unknown>),
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.active !== undefined) queryParams.active = params.active
      const { data } = await api.get<ApiResponse<GlAccount[]>>('/api/gl_accounts', {
        params: queryParams,
      })
      return data
    },
  })
}

export function useBankOpeningBalance(enabled = true) {
  return useQuery({
    queryKey: accountingKeys.glAccounts.openingBalance(),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<BankOpeningBalance>>('/api/gl_accounts/opening_balance')
      return data.data
    },
    enabled,
  })
}

export interface CreateBankOpeningBalanceInput {
  balance: number
  as_of_date: string
}

export function useCreateBankOpeningBalance() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateBankOpeningBalanceInput) => {
      const { data } = await api.post<ApiResponse<BankOpeningBalance>>('/api/gl_accounts/opening_balance', input)
      return data.data
    },
    // The opening-balance modal renders every error inline and retains input.
    meta: { suppressErrorToast: true },
    onSuccess: async (data) => {
      queryClient.setQueryData(accountingKeys.glAccounts.openingBalance(), data)
      await Promise.all([
        // This prefix covers opening status, GL lists and GL details.
        queryClient.invalidateQueries({ queryKey: accountingKeys.glAccounts.all }),
        queryClient.invalidateQueries({ queryKey: accountingKeys.journalEntries.all }),
        queryClient.invalidateQueries({ queryKey: ['balance_sheet'] }),
        queryClient.invalidateQueries({ queryKey: ['cash_flow'] }),
        queryClient.invalidateQueries({ queryKey: ['reports', 'financial_statements'] }),
        queryClient.invalidateQueries({ queryKey: bankReconciliationKeys.allTransactions.all }),
      ])
    },
  })
}

export interface CreateGlAccountInput {
  code: string
  name: string
  name_th?: string
  account_type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense'
  normal_balance: 'debit' | 'credit'
  parent_id?: number | null
  active?: boolean
}

export function useCreateGlAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateGlAccountInput) => {
      const { data } = await api.post<ApiResponse<GlAccount>>('/api/gl_accounts', {
        gl_account: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, accountingKeys.glAccounts.lists())
    },
  })
}

export interface UpdateGlAccountInput {
  id: number
  code?: string
  name?: string
  name_th?: string
  account_type?: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense'
  normal_balance?: 'debit' | 'credit'
  parent_id?: number | null
  active?: boolean
}

export function useUpdateGlAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateGlAccountInput) => {
      const { data } = await api.patch<ApiResponse<GlAccount>>(`/api/gl_accounts/${id}`, {
        gl_account: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, accountingKeys.glAccounts.lists())
      invalidateDetailQueries(queryClient, accountingKeys.glAccounts.detail(data.id))
    },
  })
}

export function useDeleteGlAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/gl_accounts/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, accountingKeys.glAccounts.lists())
    },
  })
}

export function useSeedGlAccounts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<ApiResponse<GlAccount[]>>('/api/gl_accounts/seed')
      return data
    },
    onSuccess: () => {
      removeListQueries(queryClient, accountingKeys.glAccounts.lists())
    },
  })
}

// ─── Journal Entries ──────────────────────────────────────────────────────────

export interface JournalEntryFilterParams {
  page?: number
  status?: string
  from?: string
  to?: string
  property_id?: number
}

export function useJournalEntries(page = 1, filters: Omit<JournalEntryFilterParams, 'page'> = {}) {
  const params = { page, ...filters }
  return useQuery({
    queryKey: accountingKeys.journalEntries.list(params as unknown as Record<string, unknown>),
    queryFn: async () => {
      const queryParams: Record<string, unknown> = { page }
      if (filters.status) queryParams.status = filters.status
      if (filters.from) queryParams.from = filters.from
      if (filters.to) queryParams.to = filters.to
      if (filters.property_id) queryParams.property_id = filters.property_id
      const { data } = await api.get<ApiResponse<JournalEntry[]>>('/api/journal_entries', {
        params: queryParams,
      })
      return data
    },
  })
}

export function useJournalEntry(id: number) {
  return useQuery({
    queryKey: accountingKeys.journalEntries.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<JournalEntry>>(`/api/journal_entries/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

export interface JournalLineInput {
  gl_account_id: number
  debit: number
  credit: number
  description?: string
}

export interface CreateJournalEntryInput {
  entry_date: string
  description: string
  property_id?: number | null
  journal_lines: JournalLineInput[]
}

export function useCreateJournalEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateJournalEntryInput) => {
      const { data } = await api.post<ApiResponse<JournalEntry>>('/api/journal_entries', {
        journal_entry: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, accountingKeys.journalEntries.lists())
    },
  })
}

export interface UpdateJournalEntryInput {
  id: number
  entry_date?: string
  description?: string
  property_id?: number | null
  journal_lines?: JournalLineInput[]
}

export function useUpdateJournalEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateJournalEntryInput) => {
      const { data } = await api.patch<ApiResponse<JournalEntry>>(
        `/api/journal_entries/${id}`,
        { journal_entry: input }
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, accountingKeys.journalEntries.lists())
      invalidateDetailQueries(queryClient, accountingKeys.journalEntries.detail(data.id))
    },
  })
}

export function useDeleteJournalEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/journal_entries/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, accountingKeys.journalEntries.lists())
    },
  })
}

export function usePostJournalEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ApiResponse<JournalEntry>>(
        `/api/journal_entries/${id}/post_entry`
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, accountingKeys.journalEntries.lists())
      invalidateDetailQueries(queryClient, accountingKeys.journalEntries.detail(data.id))
    },
  })
}

export function useVoidJournalEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      const { data } = await api.post<ApiResponse<JournalEntry>>(
        `/api/journal_entries/${id}/void`,
        { reason }
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, accountingKeys.journalEntries.lists())
      invalidateDetailQueries(queryClient, accountingKeys.journalEntries.detail(data.id))
    },
  })
}

// ─── Financial Statements ────────────────────────────────────────────────────

export interface IncomeStatementParams {
  start_date: string
  end_date: string
  property_id?: number
}

export function useIncomeStatement(params: IncomeStatementParams) {
  return useQuery({
    queryKey: accountingKeys.financialStatements.incomeStatement(
      params as unknown as Record<string, unknown>
    ),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<IncomeStatement>>(
        '/api/financial_statements/income_statement',
        { params }
      )
      return data.data
    },
    enabled: !!(params.start_date && params.end_date),
  })
}

export interface BalanceSheetParams {
  as_of_date: string
  property_id?: number
}

export function useBalanceSheet(params: BalanceSheetParams) {
  return useQuery({
    queryKey: accountingKeys.financialStatements.balanceSheet(
      params as unknown as Record<string, unknown>
    ),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<BalanceSheet>>(
        '/api/financial_statements/balance_sheet',
        { params }
      )
      return data.data
    },
    enabled: !!params.as_of_date,
  })
}

export interface CashFlowParams {
  start_date: string
  end_date: string
  property_id?: number
}

export function useCashFlow(params: CashFlowParams) {
  return useQuery({
    queryKey: accountingKeys.financialStatements.cashFlow(
      params as unknown as Record<string, unknown>
    ),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<CashFlow>>(
        '/api/financial_statements/cash_flow',
        { params }
      )
      return data.data
    },
    enabled: !!(params.start_date && params.end_date),
  })
}

// ─── Fixed Assets ─────────────────────────────────────────────────────────────

export interface FixedAssetFilterParams {
  page?: number
  status?: string
  property_id?: number
}

export function useFixedAssets(page = 1, filters: Omit<FixedAssetFilterParams, 'page'> = {}) {
  const params = { page, ...filters }
  return useQuery({
    queryKey: accountingKeys.fixedAssets.list(params as unknown as Record<string, unknown>),
    queryFn: async () => {
      const queryParams: Record<string, unknown> = { page }
      if (filters.status) queryParams.status = filters.status
      if (filters.property_id) queryParams.property_id = filters.property_id
      const { data } = await api.get<ApiResponse<FixedAsset[]>>('/api/fixed_assets', {
        params: queryParams,
      })
      return data
    },
  })
}

export function useFixedAsset(id: number) {
  return useQuery({
    queryKey: accountingKeys.fixedAssets.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<FixedAsset>>(`/api/fixed_assets/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

export interface CreateFixedAssetInput {
  name: string
  description?: string
  acquisition_date: string
  acquisition_cost: number
  salvage_value: number
  useful_life_months: number
  depreciation_method: 'straight_line' | 'declining_balance'
  gl_account_id?: number | null
  depreciation_gl_account_id?: number | null
  property_id?: number | null
}

export function useCreateFixedAsset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateFixedAssetInput) => {
      const { data } = await api.post<ApiResponse<FixedAsset>>('/api/fixed_assets', {
        fixed_asset: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, accountingKeys.fixedAssets.lists())
    },
  })
}

export interface UpdateFixedAssetInput {
  id: number
  name?: string
  description?: string
  acquisition_date?: string
  acquisition_cost?: number
  salvage_value?: number
  useful_life_months?: number
  depreciation_method?: 'straight_line' | 'declining_balance'
  gl_account_id?: number | null
  depreciation_gl_account_id?: number | null
  property_id?: number | null
}

export function useUpdateFixedAsset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateFixedAssetInput) => {
      const { data } = await api.patch<ApiResponse<FixedAsset>>(`/api/fixed_assets/${id}`, {
        fixed_asset: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, accountingKeys.fixedAssets.lists())
      invalidateDetailQueries(queryClient, accountingKeys.fixedAssets.detail(data.id))
    },
  })
}

export function useDeleteFixedAsset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/fixed_assets/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, accountingKeys.fixedAssets.lists())
    },
  })
}

export function useDisposeFixedAsset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, date, amount }: { id: number; date: string; amount: number }) => {
      const { data } = await api.post<ApiResponse<FixedAsset>>(
        `/api/fixed_assets/${id}/dispose`,
        { date, amount }
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, accountingKeys.fixedAssets.lists())
      invalidateDetailQueries(queryClient, accountingKeys.fixedAssets.detail(data.id))
    },
  })
}
